import React, { forwardRef, useEffect, useCallback } from 'react';
import { FlexGrid } from '@grapecity/wijmo.react.grid';

const CommonGrid = forwardRef(({ 
    children, 
    showRowNumbers = true, 
    className = "default-grid",
    itemsSource,
    hideZeroValues = true,
    allowEditing,
    frozenRows,
    ...props 
}, ref) => {
    
    const setupFormatting = useCallback(() => {
        const grid = ref?.current?.control;
        if (!grid) return;

        // 기존 핸들러 제거 (중복 방지)
        grid.formatItem.removeHandler(setupFormatting);
        
        // 포맷팅 설정
        grid.formatItem.addHandler((s, e) => {
            // 순번 설정 (1부터 시작)
            if (e.panel === s.rowHeaders && e.row > -1 && showRowNumbers) {
                e.cell.innerHTML = (e.row + 1).toString();
            }
            
            // 데이터 셀에서 0 값을 빈 셀로 표시 (편집 중이 아닐 때만)
            if (e.panel === s.cells && hideZeroValues && !s.activeEditor) {
                const cellValue = s.getCellData(e.row, e.col);
                if (cellValue === 0 || cellValue === '0') {
                    e.cell.innerHTML = '';
                }
            }
        });
    }, [ref, showRowNumbers, hideZeroValues]);

    useEffect(() => {
        setupFormatting();
        
        return () => {
            const grid = ref?.current?.control;
            if (grid) {
                grid.formatItem.removeHandler(setupFormatting);
            }
        };
    }, [setupFormatting]);

    // itemsSource가 변경될 때마다 포맷팅 다시 설정
    useEffect(() => {
        if (itemsSource) {
            // 약간의 지연을 두어 그리드가 완전히 렌더링된 후 포맷팅 적용
            const timer = setTimeout(() => {
                setupFormatting();
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [itemsSource, setupFormatting]);

    // FlexGrid에 전달할 props에서 allowEditing과 frozenRows 제외
    const { allowEditing: _, frozenRows: __, ...flexGridProps } = props;

    return (
        <FlexGrid 
            ref={ref}
            className={className}
            itemsSource={itemsSource}
            allowEditing={allowEditing}
            frozenRows={frozenRows}
            {...flexGridProps}
        >
            {children}
        </FlexGrid>
    );
});

CommonGrid.displayName = 'CommonGrid';

export default CommonGrid; 